import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle, ShieldCheck, Warning, ArrowRight } from "@phosphor-icons/react";
import LegalPage from "../../components/LegalPage";
import api from "../../lib/api";

const REQUEST_TYPES = [
  { id: "access",          label: "Right to know / access my data",                            states: "All states + UK/EU/Brazil/Canada/Australia" },
  { id: "delete",          label: "Right to delete my data",                                   states: "All states + UK/EU/Brazil/Canada" },
  { id: "correct",         label: "Right to correct my data",                                  states: "CA, VA, CO, CT, TX, OR + UK/EU" },
  { id: "portability",     label: "Right to data portability",                                 states: "All states + UK/EU/Brazil" },
  { id: "opt_out_sale",    label: "Opt out of sale / sharing for targeted ads",                states: "CA, VA, CO, CT, UT, TX, OR" },
  { id: "limit_sensitive", label: "Limit the use of my sensitive personal information",       states: "CA (CPRA §1798.121)" },
  { id: "appeal",          label: "Appeal a previous denial",                                  states: "VA, CO, CT, TX, OR" },
  { id: "other",           label: "Other privacy enquiry",                                     states: "Worldwide" },
];

export default function PrivacyRequest() {
  const [form, setForm] = useState({
    request_type: "access",
    email: "",
    full_name: "",
    state_country: "",
    authorized_agent: false,
    description: "",
    company: "", // honeypot
  });
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const gpc = typeof navigator !== "undefined" && navigator.globalPrivacyControl === true;

  // Auto-select limit-sensitive request type if user arrives with #limit-sensitive in hash.
  useEffect(() => {
    if (typeof window !== "undefined" && window.location.hash === "#limit-sensitive") {
      setForm((f) => ({ ...f, request_type: "limit_sensitive" }));
    }
  }, []);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const { data } = await api.post("/privacy/request", { ...form, gpc });
      setResult(data);
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not submit your request. Please email hello@blueroam.io.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <LegalPage
      testid="legal-privacy-request"
      eyebrow="BlueRoam Ltd · Company No. 17215030 · 128 City Road, London, EC1V 2NX"
      title="Privacy Request"
      lastUpdated="14 May 2026 · Version 1.0"
      intro="Use this webform to exercise your privacy rights under UK GDPR, EU GDPR, California CCPA/CPRA, Virginia VCDPA, Colorado CPA, Connecticut CTDPA, Utah UCPA, Texas TDPSA, Oregon OCPA, Brazil LGPD, Canada PIPEDA, Australia Privacy Act 1988, or another applicable law."
    >
      <p>
        We respond within <strong>45 days</strong> from receipt, extendable as permitted by your
        law. You can also email <a href="mailto:hello@blueroam.io">hello@blueroam.io</a> from your
        account address, or write to Privacy Office, BlueRoam Ltd, 128 City Road, London, EC1V 2NX,
        United Kingdom.
      </p>

      {result ? (
        <div
          className="my-8 rounded-3xl border-2 border-mint bg-mint-soft/40 p-6 lg:p-8"
          data-testid="privacy-request-success"
        >
          <div className="flex items-start gap-4">
            <CheckCircle size={28} weight="fill" className="text-mint flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="!mt-0 !mb-2">We received your privacy request</h3>
              <p>
                Your reference number is{" "}
                <strong className="tabular-nums" data-testid="privacy-request-id">
                  {result.id}
                </strong>
                . We'll respond by email within{" "}
                <strong>{result.sla_days} days</strong>. A confirmation has been sent to{" "}
                <strong>{form.email}</strong> — check your spam folder if it doesn't arrive in a few minutes.
              </p>
              {form.authorized_agent && (
                <p className="text-sm text-ink-700">
                  Because you marked this as an authorised agent request, we may also email you to
                  request written authorisation and verification of the consumer's identity.
                </p>
              )}
            </div>
          </div>
        </div>
      ) : (
        <form
          onSubmit={submit}
          className="my-8 rounded-3xl border-2 border-cobalt-100 bg-cobalt-50/30 p-6 lg:p-8 space-y-5"
          data-testid="privacy-request-form"
        >
          {gpc && (
            <div className="flex items-start gap-3 rounded-2xl bg-white border border-ink-200 p-4 text-sm">
              <ShieldCheck size={18} weight="bold" className="text-mint flex-shrink-0 mt-0.5" />
              <div>
                <strong>Global Privacy Control detected.</strong> We'll note your GPC signal on your
                request. For CA/CO/CT/TX/OR residents, GPC is treated as a valid opt-out of sale and sharing automatically.
              </div>
            </div>
          )}

          {/* Honeypot — keep hidden from real users but available to bots */}
          <div className="hidden" aria-hidden="true">
            <label>Company<input value={form.company} onChange={update("company")} tabIndex={-1} autoComplete="off" /></label>
          </div>

          <div>
            <label className="block text-sm font-semibold mb-1.5">Type of request *</label>
            <select
              required
              value={form.request_type}
              onChange={update("request_type")}
              className="w-full rounded-xl border border-ink-200 px-4 py-3 bg-white outline-none focus:border-cobalt-500"
              data-testid="privacy-request-type"
            >
              {REQUEST_TYPES.map((t) => (
                <option key={t.id} value={t.id}>{t.label}</option>
              ))}
            </select>
            <p className="text-xs text-ink-500 mt-1.5">
              Applies in: {REQUEST_TYPES.find((t) => t.id === form.request_type)?.states}
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold mb-1.5" htmlFor="pr-email">Email *</label>
              <input
                id="pr-email"
                required
                type="email"
                value={form.email}
                onChange={update("email")}
                placeholder="you@example.com"
                className="w-full rounded-xl border border-ink-200 px-4 py-3 bg-white outline-none focus:border-cobalt-500"
                data-testid="privacy-request-email"
              />
              <p className="text-xs text-ink-500 mt-1.5">Used to send our response. Please use the email on your account where possible.</p>
            </div>
            <div>
              <label className="block text-sm font-semibold mb-1.5" htmlFor="pr-name">Full name (optional)</label>
              <input
                id="pr-name"
                type="text"
                value={form.full_name}
                onChange={update("full_name")}
                className="w-full rounded-xl border border-ink-200 px-4 py-3 bg-white outline-none focus:border-cobalt-500"
                data-testid="privacy-request-name"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold mb-1.5" htmlFor="pr-state">State or country of residence (optional)</label>
            <input
              id="pr-state"
              type="text"
              value={form.state_country}
              onChange={update("state_country")}
              placeholder="e.g. California, United Kingdom, Brazil"
              className="w-full rounded-xl border border-ink-200 px-4 py-3 bg-white outline-none focus:border-cobalt-500"
              data-testid="privacy-request-state"
            />
            <p className="text-xs text-ink-500 mt-1.5">Helps us apply the correct legal framework to your request.</p>
          </div>

          <div>
            <label className="block text-sm font-semibold mb-1.5" htmlFor="pr-desc">Description (optional)</label>
            <textarea
              id="pr-desc"
              rows={4}
              value={form.description}
              onChange={update("description")}
              placeholder="Tell us what you'd like us to do, or any details that help us locate your data."
              className="w-full rounded-xl border border-ink-200 px-4 py-3 bg-white outline-none focus:border-cobalt-500 resize-y"
              data-testid="privacy-request-description"
            />
          </div>

          <label className="flex items-start gap-3 text-sm bg-white rounded-2xl border border-ink-200 p-4 cursor-pointer">
            <input
              type="checkbox"
              checked={form.authorized_agent}
              onChange={update("authorized_agent")}
              className="mt-1"
              data-testid="privacy-request-agent"
            />
            <span>
              <strong>I am submitting this on behalf of someone else as an authorised agent.</strong>
              <span className="block text-ink-700 mt-1 text-xs">
                We'll follow up to request written authorisation and verification of both you and the consumer per CPRA §1798.135(c) and equivalent laws.
              </span>
            </span>
          </label>

          {error && (
            <div className="flex items-start gap-2.5 text-sm rounded-2xl bg-coral-soft/40 border border-coral-soft p-4" data-testid="privacy-request-error">
              <Warning size={18} weight="bold" className="text-coral-deep flex-shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          <button
            type="submit"
            disabled={submitting}
            className="btn-primary text-base group"
            data-testid="privacy-request-submit"
          >
            {submitting ? "Submitting…" : "Submit my privacy request"}
            <ArrowRight size={16} weight="bold" className="transition-transform group-hover:translate-x-0.5" />
          </button>
          <p className="text-xs text-ink-500 !mt-3">
            By submitting, you confirm the information is true and complete to the best of your knowledge.
            We process this request under the legal bases set out in our{" "}
            <Link to="/legal/privacy">Privacy Policy</Link>.
          </p>
        </form>
      )}

      <h2>What happens next</h2>
      <ol>
        <li>You'll receive an immediate email confirmation with your reference number.</li>
        <li>Our Privacy team verifies your identity using information already on file. We may request additional verification for sensitive requests.</li>
        <li>We respond by email within <strong>45 days</strong>. Where the law permits an extension, we'll let you know.</li>
        <li>If we deny your request (in whole or in part), and you live in VA/CO/CT/TX/OR, you can appeal by replying to our decision email — we respond to appeals within 60 days.</li>
      </ol>

      <h2>Other channels</h2>
      <ul>
        <li><strong>Email:</strong> <a href="mailto:hello@blueroam.io">hello@blueroam.io</a></li>
        <li><strong>Compliance / DPO:</strong> <a href="mailto:compliance@blueroam.io">compliance@blueroam.io</a></li>
        <li><strong>Post:</strong> Privacy Office, BlueRoam Ltd, 128 City Road, London, EC1V 2NX, United Kingdom</li>
        <li><strong>EU representative (Art. 27 GDPR):</strong> Panos Vlahos, Patron 4, 4044, Limassol, Cyprus</li>
      </ul>
      <p>
        Full details of your rights by jurisdiction:{" "}
        <Link to="/legal/privacy">Privacy Policy</Link> · <Link to="/legal/us-privacy">US Privacy Notice</Link>.
      </p>
    </LegalPage>
  );
}
